'use client';

import { Icon } from '@magicschool/spellbook/components/Icon';
import { Label } from '@magicschool/spellbook/components/Label';
import { Text } from '@magicschool/spellbook/components/Text';
import { useId } from '@magicschool/spellbook/hooks/useId';
import { cn, mergeIds } from '@magicschool/spellbook/lib/utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import * as CheckboxPrimitive from 'radix-ui/checkbox';
import { type ReactNode, useState } from 'react';

export type CheckboxProps = Omit<React.ComponentProps<typeof CheckboxPrimitive.Root>, 'color'> &
  VariantProps<typeof checkboxVariants> & {
    /** The text label displayed next to the checkbox. Required for accessibility — linked via `aria-labelledby`. */
    label: ReactNode;
    /** When `false`, visually hides the label while keeping it accessible to screen readers. Default: `true`. */
    showLabel?: boolean;
    /** Supplementary text displayed below the label. Linked to the checkbox via `aria-describedby`. */
    description?: string;
    /** When `true`, marks the checkbox as invalid and sets `aria-invalid="true"`. Use with form validation. */
    invalid?: boolean;
    /** The value attribute for the underlying input, useful when the checkbox is part of a form submission. */
    value?: string;
    /** Shows an asterisk after the label of a `required` checkbox. */
    showRequiredIndicator?: boolean;
  };

export const checkboxVariants = cva([
  'peer',
  'size-4',
  'm-1',
  'shrink-0',
  'rounded-sm',
  'transition-border',
  'outline-none',
  'cursor-pointer',
  'border-[1.5px]',
  'aspect-square',
  'border-checkbox',
  'bg-surface-background-0',
  'hover:bg-surface-background-2',
  'hover:border-checkbox-hover',
  'active:bg-surface-background-3',
  'aria-checked:bg-primary-solid-background',
  'aria-checked:border-transparent',
  'aria-checked:hover:bg-primary-solid-background-hover',
  'aria-checked:active:bg-primary-solid-background-active',
  'aria-checked:text-primary-solid-foreground-text',
  'aria-[checked=mixed]:bg-checkbox',
  'aria-[checked=mixed]:border-transparent',
  'aria-[checked=mixed]:hover:bg-checkbox-hover',
  'aria-[checked=mixed]:active:bg-checkbox-hover',
  'aria-[checked=mixed]:text-default-solid-foreground-text',
  'focus-visible-ring',
  'aria-invalid:!border-error-stroke',
  'aria-invalid:hover:!border-error-stroke',
  'disabled:cursor-not-allowed',
  'disabled:border-disabled-foreground',
  'disabled:bg-disabled-background',
  'aria-checked:disabled:bg-disabled-foreground',
  'aria-[checked=mixed]:disabled:bg-disabled-foreground',
]);

export const checkboxDescriptionVariants = cva('flex flex-col items-start justify-start mt-0.75', {
  variants: {
    showLabel: {
      false: 'sr-only',
    },
  },
});

/**
 * Single checkbox input with a label and optional description. Built on Radix UI Checkbox.
 *
 * ### When to use
 * - Boolean opt-in/opt-out choices (e.g., "I agree to terms", "Subscribe to newsletter")
 * - Selecting one or more items from a list (e.g., feedback types, feature toggles)
 * - Standalone toggleable settings where the choice is confirmed by a separate action
 *
 * ### When NOT to use
 * - For an instant on/off toggle that takes effect immediately: use `Switch` instead
 * - For selecting one option from a mutually exclusive set: use `RadioGroup` instead
 * - For grouping multiple related checkboxes with shared state: wrap with `CheckboxGroup` instead
 *
 * ### Checkbox component comparison
 * | Component | Selection model | Effect timing | Example use case |
 * | --- | --- | --- | --- |
 * | `Checkbox` | Single boolean | Deferred (confirmed later) | "I agree to terms", opt-in/opt-out |
 * | `CheckboxGroup` | Multiple from a list | Deferred (confirmed later) | Preference lists, multi-select filters |
 * | `Switch` | Single boolean | Immediate (takes effect on toggle) | Feature toggles, enable/disable settings |
 * | `SwitchGroup` | Multiple independent toggles | Immediate (takes effect on toggle) | Notification preferences, admin settings |
 * | `RadioGroup` | Exactly one from a set | Deferred (confirmed later) | Export format, quiz answer, severity level |
 *
 * **Deferred vs. immediate**: Use `Checkbox` / `CheckboxGroup` / `RadioGroup` when the user's choices are confirmed by a separate
 * action (e.g., a submit button). Use `Switch` / `SwitchGroup` when the toggle should take effect as soon as the user flips it.
 *
 * ### API
 * - `label`: Required. The visible label text, linked to the checkbox via `aria-labelledby`.
 * - `showLabel`: When `false`, visually hides the label while keeping it accessible to screen readers.
 * - `description`: Optional helper text below the label, linked via `aria-describedby`.
 * - `invalid`: Sets `aria-invalid="true"` and applies error border styling.
 * - `checked`: Controlled checked state. Accepts `true`, `false`, or `"indeterminate"`.
 * - `defaultChecked`: Initial checked state for uncontrolled usage.
 * - `onCheckedChange`: Callback fired when the checked state changes. Receives the new `boolean` value.
 *
 * ### Visual states
 * - **Unchecked**: Default state with a bordered empty box
 * - **Checked**: Filled with primary color and a check icon
 * - **Indeterminate**: Filled with primary color and a minus icon, for partial selection
 * - **Disabled**: Muted background and foreground, cursor changes to `not-allowed`
 * - **Invalid**: Error-colored border via `aria-invalid`
 * - **Focused**: Visible focus ring for keyboard navigation
 *
 * ### Accessibility
 * - `label` is required and automatically linked via `aria-labelledby`
 * - `description` is linked via `aria-describedby` when provided
 * - Supports `checked="indeterminate"` for parent checkboxes where only some children are selected
 * - Keyboard accessible: Space to toggle, Tab to navigate
 * - Wrapping in a `Tooltip.Trigger` is not recommended — the tooltip's `data-state` can conflict with Radix internals and the toggle interaction can feel awkward alongside tooltip hover/focus behavior. If you must, it is safe for styling (checked/indeterminate styles use `aria-checked`, not `data-state`), but never wrap a disabled Checkbox in a Tooltip — disabled elements cannot receive focus or hover, making the tooltip inaccessible
 *
 */
export const Checkbox: React.FC<CheckboxProps> = ({
  className,
  defaultChecked,
  checked,
  label,
  showLabel = true,
  invalid,
  description,
  id: providedId,
  onCheckedChange,
  required,
  showRequiredIndicator = true,
  'aria-describedby': ariaDescribedBy,
  ...props
}) => {
  const id = useId(providedId);
  const descriptionId = `${id}-description`;
  const labelId = `${id}-label`;
  const [isPressed, setIsPressed] = useState(defaultChecked ?? checked ?? undefined);
  const iconName = (checked ?? isPressed) === 'indeterminate' ? 'minus' : 'check';

  const handleCheckedChange = (value: boolean) => {
    setIsPressed(value);
    if (onCheckedChange) {
      onCheckedChange(value);
    }
  };

  return (
    <div className="flex group items-start justify-start overflow-hidden gap-1.5" data-disabled={props.disabled}>
      <CheckboxPrimitive.Root
        data-spellbook-name="checkbox"
        className={cn(checkboxVariants(), className)}
        checked={checked}
        defaultChecked={defaultChecked}
        id={id}
        required={required}
        {...(invalid ? { 'aria-invalid': true } : {})}
        aria-describedby={mergeIds(description ? descriptionId : undefined, ariaDescribedBy)}
        {...(label ? { 'aria-labelledby': labelId } : {})}
        {...props}
        onCheckedChange={handleCheckedChange}
      >
        <CheckboxPrimitive.Indicator data-spellbook-name="checkbox-indicator" className="flex items-center justify-center text-current">
          <Icon name={iconName} strokeWidth={3} className="size-2.75 min-w-2.75 min-h-2.75" />
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
      {label && (
        <div className={checkboxDescriptionVariants({ showLabel })}>
          <Label
            className="cursor-pointer group-data-[disabled=true]:!text-current"
            htmlFor={id}
            id={labelId}
            required={required && showRequiredIndicator}
          >
            {label}
          </Label>
          {description && (
            <Text color="default-soft" variant="body-2" element="span" id={descriptionId}>
              {description}
            </Text>
          )}
        </div>
      )}
    </div>
  );
};
